import React, { useState } from 'react'
import { generateOpenApiSpec } from '../../../../utils/openapi-spec'
import type { Language, LocalizationStrings } from '../../../../config/localization'

interface OpenApiSpecModalProps {
  workflowId: string
  onClose: () => void
  language: Language
  t: LocalizationStrings
}

const OpenApiSpecModal: React.FC<OpenApiSpecModalProps> = ({
  workflowId,
  onClose,
  language,
  t
}) => {
  const [copied, setCopied] = useState(false)

  const spec = generateOpenApiSpec({
    baseUrl: window.location.origin,
    workflowId
  })

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(spec)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] animate-fadeIn"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-4xl max-h-[90vh] mx-4 flex flex-col animate-scaleIn">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {t.openApiModal?.title || 'API Documentation'}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopy}
              className={`px-4 py-2 rounded text-sm transition-colors ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              }`}
            >
              {copied
                ? (t.openApiModal?.copied || 'Copied!')
                : (t.openApiModal?.copy || 'Copy')}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded transition-colors text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <pre className="bg-gray-950 border border-gray-800 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto font-mono whitespace-pre">
            {spec}
          </pre>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
          >
            {t.openApiModal?.close || 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default OpenApiSpecModal
